import { useState } from 'react';
import { Outlet, NavLink, Link } from 'react-router-dom';
import { useAuthStore } from '@/core/stores/authStore';
import {
  LayoutDashboard,
  Users,
  Shield,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronDown,
  Home,
  Store,
  FileText,
  BarChart3,
  Server,
  ClipboardList,
  ExternalLink,
  Scale,
  Stethoscope,
} from 'lucide-react';

/**
 * AdminLayout - Layout for the admin portal
 */
export default function AdminLayout() {
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Users', href: '/admin/users', icon: Users },
    { name: 'Families', href: '/admin/families', icon: Home },
    { name: 'Moderation', href: '/admin/moderation', icon: Shield },
    { name: 'Vendors', href: '/admin/vendors', icon: Store },
    { name: 'Resources', href: '/admin/resources', icon: FileText },
    { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
    { name: 'System', href: '/admin/system', icon: Server },
    { name: 'Audit Logs', href: '/admin/audit', icon: ClipboardList },
  ];

  // Portal switcher - allows admin to navigate to other portals
  const portals = [
    { name: 'Family Portal', href: '/family/dashboard', icon: Home, color: 'from-sage to-sage-dark' },
    { name: 'Vendor Portal', href: '/vendor/dashboard', icon: Store, color: 'from-terracotta to-terracotta-dark' },
    { name: 'Attorney Portal', href: '/attorney/dashboard', icon: Scale, color: 'from-navy to-navy-dark' },
    // { name: 'Provider Portal', href: '/provider/dashboard', icon: Stethoscope, color: 'from-info to-info/70' },
  ];

  return (
    <div className="min-h-screen bg-cream">
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-navy/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full bg-navy border-r border-white/10
          transition-all duration-smooth
          ${sidebarOpen ? 'w-64' : 'w-20'}
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          <Link to="/admin/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-info to-info/70 rounded-xl flex items-center justify-center text-white flex-shrink-0">
              <Shield className="w-5 h-5" />
            </div>
            {sidebarOpen && (
              <div>
                <span className="font-display text-lg font-semibold text-white">BrightPath</span>
                <span className="block text-xs text-white/60">Admin Portal</span>
              </div>
            )}
          </Link>
          
          <button 
            className="lg:hidden p-2 rounded-md text-white/70 hover:bg-white/10"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {/* Admin Navigation */}
          <div className="space-y-1">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2.5 rounded-md transition-fast
                  ${isActive
                    ? 'bg-info text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }
                `}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="font-medium">{item.name}</span>}
              </NavLink>
            ))}
          </div>

          {/* Portal Switcher */}
          <div className="mt-8 pt-6 border-t border-white/10">
            {sidebarOpen && (
              <h3 className="px-3 mb-3 text-xs font-semibold text-white/50 uppercase tracking-wider">
                Switch Portal
              </h3>
            )}
            <div className="space-y-1">
              {portals.map((portal) => (
                <Link
                  key={portal.name}
                  to={portal.href}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md text-white/70 hover:bg-white/10 hover:text-white transition-fast group"
                >
                  <div className={`w-5 h-5 rounded flex items-center justify-center bg-gradient-to-br ${portal.color} flex-shrink-0`}>
                    <portal.icon className="w-3 h-3 text-white" />
                  </div>
                  {sidebarOpen && (
                    <>
                      <span className="font-medium flex-1">{portal.name}</span>
                      <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </nav>

        <div className="hidden lg:flex p-3 border-t border-white/10">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex items-center justify-center w-full py-2 px-3 rounded-md text-white/60 hover:bg-white/10 transition-fast"
          >
            <Menu className="w-5 h-5" />
            {sidebarOpen && <span className="ml-2 text-sm">Collapse</span>}
          </button>
        </div>
      </aside>

      <div className={`transition-all duration-smooth ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        <header className="sticky top-0 z-30 h-16 bg-cream/95 backdrop-blur-md border-b border-warm-gray/10">
          <div className="h-full px-4 lg:px-8 flex items-center justify-between">
            <button
              className="lg:hidden p-2 rounded-md hover:bg-cream-dark"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex-1" />

            <div className="flex items-center gap-4">
              <button className="relative p-2 rounded-md hover:bg-cream-dark transition-fast">
                <Bell className="w-5 h-5 text-navy" />
              </button>

              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-cream-dark transition-fast"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-info to-info/70 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </div>
                  <ChevronDown className="w-4 h-4 text-warm-gray" />
                </button>

                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-medium border border-warm-gray/10 py-1 z-20">
                      <div className="px-4 py-3 border-b border-warm-gray/10">
                        <p className="text-sm font-medium text-navy">{user?.first_name} {user?.last_name}</p>
                        <p className="text-xs text-warm-gray">{user?.email}</p>
                        <span className="inline-block mt-1 px-2 py-0.5 bg-info/10 text-info text-xs rounded-full font-medium">
                          Admin
                        </span>
                      </div>
                      <button
                        onClick={() => logout()}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm text-terracotta hover:bg-cream transition-fast"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

